import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import {
  Box,
  Button,
  Chip,
  FormControlLabel,
  IconButton,
  LinearProgress,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'

import { toast } from '../../components/LsToast'
import { ScreenWrapper } from '../../components/ScreenWrapper'
import { getInstructors } from '../../service/instructors.service'
import { InstructorDto, LANGUAGE_CODES, LanguageLevel, LICENCE_DISCIPLINES, LicenceLevel } from '../../service/types'
import { InstructorDialog } from './InstructorDialog'

export const Instructors: FC = () => {
  const { t } = useTranslation()
  const [instructors, setInstructors] = useState<InstructorDto[]>([])
  const [loading, setLoading] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [selectedInstructor, setSelectedInstructor] = useState<InstructorDto | null>(null)
  const [showInactive, setShowInactive] = useState(false)

  const loadInstructors = useCallback(async () => {
    setLoading(true)
    try {
      const response = await getInstructors(showInactive ? undefined : true)
      setInstructors(response.data)
    } catch {
      toast.error(t('general.notification.unexpectedError'))
    } finally {
      setLoading(false)
    }
  }, [t, showInactive])

  useEffect(() => {
    void loadInstructors()
  }, [loadInstructors])

  const sortedInstructors = useMemo(
    () =>
      [...instructors].sort((a, b) => {
        if (a.isActive !== b.isActive) return a.isActive ? -1 : 1
        return a.name.localeCompare(b.name)
      }),
    [instructors],
  )

  const handleAdd = () => {
    setSelectedInstructor(null)
    setDialogOpen(true)
  }

  const handleEdit = (instructor: InstructorDto) => {
    setSelectedInstructor(instructor)
    setDialogOpen(true)
  }

  return (
    <ScreenWrapper
      title={t('instructors.title')}
      filter={
        <Box display="flex" alignItems="center" gap={2}>
          <FormControlLabel
            control={<Switch checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />}
            label={t('instructors.showInactive')}
          />
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd}>
            {t('instructors.addInstructor')}
          </Button>
        </Box>
      }
    >
      {loading && <LinearProgress sx={{ mt: 2 }} />}
      <TableContainer component={Paper} sx={{ mt: loading ? 0 : 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('instructors.form.name')}</TableCell>
              <TableCell>{t('instructors.form.phone')}</TableCell>
              <TableCell>{t('instructors.form.email')}</TableCell>
              <TableCell>{t('instructors.form.languages')}</TableCell>
              <TableCell>{t('instructors.form.licences')}</TableCell>
              <TableCell align="right"></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading && sortedInstructors.length === 0 && (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography variant="body2" color="text.secondary">
                    {t('instructors.noResults')}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {sortedInstructors.map((instructor) => (
              <TableRow key={instructor.id} sx={{ opacity: instructor.isActive ? 1 : 0.5 }}>
                <TableCell>{instructor.name}</TableCell>
                <TableCell>{instructor.phone ?? '-'}</TableCell>
                <TableCell>{instructor.email ?? '-'}</TableCell>
                <TableCell>
                  <Box display="flex" gap={0.5} flexWrap="wrap">
                    {LANGUAGE_CODES.filter(
                      (code) => instructor.languages?.[code] && instructor.languages[code] !== LanguageLevel.None,
                    ).map((code) => (
                      <Chip
                        key={code}
                        label={`${t(`languages.${code}`)}: ${t(`languageLevel.${instructor.languages[code]}`)}`}
                        size="small"
                      />
                    ))}
                    {(!instructor.languages ||
                      LANGUAGE_CODES.every(
                        (c) => !instructor.languages?.[c] || instructor.languages[c] === LanguageLevel.None,
                      )) &&
                      '—'}
                  </Box>
                </TableCell>
                <TableCell>
                  <Box display="flex" gap={0.5} flexWrap="wrap">
                    {LICENCE_DISCIPLINES.filter(
                      (d) => instructor.licences?.[d] && instructor.licences[d] !== LicenceLevel.None,
                    ).map((d) => (
                      <Chip
                        key={d}
                        label={`${t(`licences.${d}`)}: ${t(`licenceLevel.${instructor.licences[d]}`)}`}
                        size="small"
                      />
                    ))}
                    {(!instructor.licences ||
                      LICENCE_DISCIPLINES.every(
                        (d) => !instructor.licences?.[d] || instructor.licences[d] === LicenceLevel.None,
                      )) &&
                      '—'}
                  </Box>
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => handleEdit(instructor)}>
                    <EditIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <InstructorDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSaved={() => void loadInstructors()}
        instructor={selectedInstructor}
      />
    </ScreenWrapper>
  )
}
